import { Injectable } from '@angular/core';

/** Deliberately sessionStorage, never localStorage - see the class comment. */
const STORAGE_KEY = 'tl.tab.organizationId';

/**
 * The organization THIS TAB intends to be in, held across a page reload.
 *
 * A tab's organization is a claim on its own in-memory token, which a reload destroys. The
 * APP_INITIALIZER then trades the refresh cookie for a new token, and that rebuilds claims from
 * `User.LastSelectedOrganizationId` - a column shared by every session the user has open. So without
 * this a reloaded tab silently adopts whichever organization was picked last, in any tab, on any
 * machine. This is what lets two tabs sit in two organizations and survive F5.
 *
 * `sessionStorage`, not `localStorage`, and that is the whole mechanism: sessionStorage is scoped to
 * the tab by definition, so two tabs cannot see each other's value. localStorage would be shared and
 * would reintroduce exactly the cross-tab coupling this removes. (A duplicated tab inherits a copy,
 * which is the behaviour you want - it opens where you were.)
 *
 * It holds INTENT, not state: what this tab last chose to be in, whether by the user picking an
 * organization, by following another tab under the `converge` behaviour, or by the user accepting a
 * change under `notify`. Anything that changes what this tab means to be scoped to writes here;
 * anything that merely observes another tab's change does not. Get that wrong and `converge` stops
 * converging, or `notify`'s Reload reloads you into the organization you were already in.
 *
 * The value is a request. The server validates it through the same organization-access check an
 * ordinary switch goes through and ignores it otherwise, so nothing here can reach an organization
 * the user could not select by hand.
 *
 * Every access is wrapped: sessionStorage throws in some privacy modes, and a session that cannot
 * remember its organization must still work - it simply falls back to the shared column, which is
 * the behaviour that existed before this.
 */
@Injectable({
  providedIn: 'root',
})
export class TabOrganizationService {
  /** The organization this tab intends to be in, or null if it has not said. */
  public get(): number | null {
    try {
      const stored = sessionStorage.getItem(STORAGE_KEY);
      if (!stored) return null;

      const organizationId = Number(stored);
      return Number.isInteger(organizationId) && organizationId > 0
        ? organizationId
        : null;
    } catch {
      return null;
    }
  }

  /** Records what this tab now intends to be scoped to. */
  public set(organizationId: number | null | undefined): void {
    try {
      if (!organizationId || organizationId <= 0) {
        sessionStorage.removeItem(STORAGE_KEY);
        return;
      }

      sessionStorage.setItem(STORAGE_KEY, String(organizationId));
    } catch {
      // Unavailable storage costs this tab its organization across a reload and nothing else.
    }
  }

  /**
   * Forgets this tab's intent, so the next refresh takes the shared column's organization.
   *
   * Used on logout: the next person to sign in on this tab must not inherit the last one's
   * organization.
   */
  public clear(): void {
    this.set(null);
  }
}
